import store from '@/shared/store'
import { ref, computed, onBeforeMount } from '@vue/composition-api'
import { fetchContracts, saveContract, deleteContract } from '@/shared/services/contractService'
// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'
import i18n from '@hw-core/libs/i18n'
import { getAreaById } from '@/shared/services/areaService'

export default function useCampaignList() {
  // Use toast
  const toast = useToast()
  const docItem = ref(null)
  const processer = ref(null)

  // Table Handlers
  const campaignList = ref([])
  const refCampaignListTable = ref(null)
  const tableColumns = [
    { key: 'created', label: i18n.t('contract.labels.created') },
    { key: 'name', label: i18n.t('name') },
    { key: 'startDate', label: i18n.t('contract.labels.startDate') },
    { key: 'endDate', label: i18n.t('contract.labels.endDate') },
    { key: 'salary', label: i18n.t('contract.labels.salary') },
    { key: 'price', label: i18n.t('contract.labels.price') },
    { key: 'actions', label: i18n.t('actions') },
  ]

  const selectedAccount = computed(() => store.state.appAccount.selectedAccount)

  onBeforeMount(() => {
    store.dispatch('appAuth/getCurrentUser').then(res => {
      const user = JSON.parse(JSON.stringify(res))
      processer.value = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      }
    })
  })

  const refetchData = () => {
    refCampaignListTable.value.refresh()
  }

  const fetchCampaignList = (ctx, callback) => {
    const accountId = selectedAccount.value.value.id

    fetchContracts({ accountId, type: 'campaign' }).then(async campaigns => {
      const data = await Promise.all(
        campaigns.map(async campaign => {
          if (!campaign.areas || campaign.areas.length < 1) {
            return campaign
          }
          const areas = await Promise.all(campaign.areas.map(area => getAreaById(area)))
          return { ...campaign, areas }
        })
      )
      campaignList.value = data
      callback(data)
    })
  }

  const createOrUpdateCampaign = ({ data, callback }) => {
    saveContract(data)
      .then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Saved campaign',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: i18n.t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        refetchData()
        callback()
      })
  }

  const confirmDelete = () => {
    if (docItem.value) {
      deleteContract(docItem.value.id)
        .then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Campaign deleted',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: i18n.t('error'),
              text: i18n.t('something_went_wrong'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          refetchData()
        })
    }
  }

  const checkCampaignExist = data => {
    if (!data.startDate || !data.endDate) {
      return false
    }
    let exist = false
    const { id } = data
    const startDate = data.startDate.getTime()
    const endDate = data.endDate.getTime()

    campaignList.value.forEach(campaign => {
      const campaignStartDate = campaign.startDate.toDate().getTime()
      const campaignEndDate = campaign.endDate.toDate().getTime()
      if (
        campaign.id !== id &&
        ((campaignStartDate <= startDate && startDate <= campaignEndDate) ||
          (campaignStartDate <= endDate && endDate <= campaignEndDate))
      ) {
        exist = true
      }
    })
    return exist
  }

  return {
    docItem,
    refCampaignListTable,
    tableColumns,
    campaignList,

    refetchData,
    fetchCampaignList,
    createOrUpdateCampaign,
    confirmDelete,
    checkCampaignExist,
  }
}
